import React from 'react';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { ActionMenu, ActionMenuItem, ActionMenuList, ActionMenuListTitle, ActionMenuSection } from '../../../ActionMenu';
import { Icon } from '../../../Icon';

const GUTTER_MOBILE_WIDTH = 22;
const GUTTER_MOBILE = toRem(GUTTER_MOBILE_WIDTH);

interface IDropdownProps {
  isVisible?: boolean;
}

export const HeaderDropdownLayout = styled(ActionMenu)<IDropdownProps>`
  position: fixed;
  top: 100%;
  left: 0;
  z-index: ${theme.zIndex.header + 20};
  display: flex;
  transition:
    top ${theme.transition.nav},
    width ${theme.transition.button.default};
  overflow: scroll;

  ${({ isVisible }) =>
    isVisible &&
    css`
      top: 0;
    `}

  @media (min-width: ${theme.breakpoint.small}px) {
    top: ${toRem(63)};
    left: ${toRem(72)};
    overflow: hidden;
  }
`;

interface IHeaderDropdownItemProps {
  fluidHeight?: boolean;
}

export const HeaderDropdownItem = styled(ActionMenuItem)<IHeaderDropdownItemProps>`
  flex-grow: 0;
  flex-shrink: 0;
  height: ${toRem(theme.dropdown.item.height.mobile)};
  overflow: hidden;

  @media (min-width: ${theme.breakpoint.small}px) {
    height: ${toRem(theme.dropdown.item.height.desktop)};
  }

  ${({ fluidHeight }) =>
    fluidHeight &&
    css`
      height: auto;
      min-height: ${toRem(theme.dropdown.item.height.mobile)};
      padding-top: ${toRem(9)};
      padding-bottom: ${toRem(9)};
    `}
`;

export const RoleList = styled(ActionMenuList).attrs({
  listType: 'parent',
})`
  @media (max-width: ${theme.breakpoint.small - 1}px) {
    padding: 0 0 ${GUTTER_MOBILE};
  }
`;

interface IRoleListTitleProps {
  type?: 'child' | 'parent';
}

export const RoleListTitle = styled(ActionMenuListTitle)<IRoleListTitleProps>`
  color: ${theme.color.neutral[500]};

  @media (max-width: ${theme.breakpoint.small - 1}px) {
    padding-left: ${toRem(GUTTER_MOBILE_WIDTH + 11)};
    padding-right: ${GUTTER_MOBILE};
    color: ${theme.color.neutral[500]};
  }

  ${({ type }) =>
    type === 'child' &&
    css`
      @media (max-width: ${theme.breakpoint.small - 1}px) {
        padding: ${toRem(11)} ${toRem(11)} ${toRem(13.75)};
        color: ${theme.color.neutral[800]};
      }
    `}
`;

interface IRoleItemProps {
  label: string;
  isActive?: boolean;
  onClick?: () => void;
}

export const RoleItemWrap = styled.div`
  flex-shrink: 0;
  width: 100%;
  padding: 0 ${GUTTER_MOBILE};

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: 0 0;
  }
`;

const RoleItemLayout = styled(HeaderDropdownItem)<Partial<IRoleItemProps>>`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;

  @media (max-width: ${theme.breakpoint.small - 1}px) {
    ${({ isActive }) =>
      isActive &&
      css`
        background-color: transparent;

        &:hover {
          background-color: transparent;
        }
      `}
  }
`;

const RoleItemHeader = styled.span<Partial<IRoleItemProps>>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${toRem(theme.dropdown.item.height.mobile)};

  svg {
    fill: ${theme.color.neutral[800]};
    transform: none;
    transition: transform ${theme.transition.button.fast};
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    height: ${toRem(theme.dropdown.item.height.desktop)};

    svg {
      fill: ${theme.color.neutral[300]};
    }
  }

  ${({ isActive }) =>
    isActive &&
    css`
      svg {
        transform: rotate(90deg);
      }

      @media (min-width: ${theme.breakpoint.small}px) {
        svg {
          transform: none;
        }
      }
    `}
`;

export const RoleItem = ({ label, isActive, onClick, ...rest }: IRoleItemProps) => (
  <RoleItemWrap {...rest}>
    <RoleItemLayout isActive={isActive} onClick={onClick}>
      <RoleItemHeader isActive={isActive}>
        {label}
        <Icon icon="Next" width={8} height={13} />
      </RoleItemHeader>
    </RoleItemLayout>
  </RoleItemWrap>
);

export const RoleOptionsMobile = styled.section<Partial<IRoleItemProps>>`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: 0;
  background-color: transparent;
  transition: height 10000ms;

  ${({ isActive }) =>
    isActive &&
    css`
      height: auto;
      margin-bottom: ${toRem(22)};
      padding: ${toRem(16.5)} ${toRem(22)};
      border-top: ${toRem(1)} solid ${theme.color.neutral[200]};
      border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
      background-color: ${theme.color.neutral[50]};
    `}

  @media (min-width: ${theme.breakpoint.small}px) {
    display: none;
  }
`;

export const RoleOptionsDesktop = styled(ActionMenuSection)`
  display: none;
  overflow-x: none;

  @media (min-width: ${theme.breakpoint.small}px) {
    display: flex;
  }
`;

export const RoleOptionsList = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: auto;
  padding: 0;
`;

const SIIconSize = 19.2;
const SIPadRight = 11;

const SelectableHeaderDropdownItem = styled(HeaderDropdownItem)`
  @media (min-width: ${theme.breakpoint.small}px) {
    padding-right: ${toRem(SIIconSize + SIPadRight + 5.5)};
  }
`;

const SelectableItemWrapper = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  flex-grow: 0;
  flex-shrink: 1;
  width: 100%;
`;

const SelectableItemLabel = styled.span`
  width: 100%;
  padding: ${toRem(1)} 0;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;

  @media (min-width: ${theme.breakpoint.small}px) {
    max-width: 100%;
  }
`;

const SelectableItemDescription = styled.p`
  width: 100%;
  padding: ${toRem(1)} 0;
  margin-top: ${toRem(5.5)};
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(9.25)};
  line-height: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  color: ${theme.color.neutral[900]};
`;

const SelectedIconContainer = styled.span`
  position: absolute;
  top: 0;
  right: ${toRem(SIPadRight)};
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${toRem(SIIconSize)};
`;

const SelectedIcon = styled(Icon).attrs({
  icon: 'Success',
  width: SIIconSize,
  height: SIIconSize,
  fill: theme.color.secondary[400],
})`
  flex-shrink: 0;
`;

interface ISelectableItemProps {
  description?: string;
  isActive?: boolean;
  label: string;
  onClick?: () => void;
}

export const SelectableItem = ({ description, isActive, label, onClick, ...rest }: ISelectableItemProps) => (
  <SelectableHeaderDropdownItem fluidHeight={!!description} onClick={onClick} {...rest}>
    <SelectableItemWrapper>
      <SelectableItemLabel>{label}</SelectableItemLabel>
      {description && <SelectableItemDescription>{description}</SelectableItemDescription>}
    </SelectableItemWrapper>

    {isActive && (
      <SelectedIconContainer>
        <SelectedIcon data-cy="header-dropdown-item-selected-icon" />
      </SelectedIconContainer>
    )}
  </SelectableHeaderDropdownItem>
);

export const NavItems = styled(ActionMenuList)`
  @media (max-width: ${theme.breakpoint.small - 1}px) {
    padding: ${GUTTER_MOBILE};
  }
`;

export const DropdownDismiss = styled.div.attrs({
  'aria-hidden': true,
})`
  position: fixed;
  top: 0;
  left: 0;
  z-index: ${theme.zIndex.header + 1};
  width: 100%;
  height: 100%;
  background-color: transparent;
`;
